/**
 * Fallidos agrupados por código de Twilio/Meta, con el título y la acción que arma el backend
 * (`codigos.util.ts`). «Ver a quiénes» filtra la bitácora SÓLO por código: así junta `fallido` y
 * `no_entregado` igual que esta tabla (un 30036, por ejemplo, llega como `no_entregado`). La fila
 * SIN código (un rechazo o un `failed` sin ErrorCode) filtra con `sin_codigo` (`valorCodigo`), nunca
 * con el código vacío: ése no filtra, y la bitácora abría con todos los mensajes de la campaña.
 * «No se cobra» sólo sale si la fila es `fallido` de código exento (`fallidoSinCargo`): a un
 * `no_entregado` se le retiene el cargo de Twilio sea cual sea el código.
 *
 * Una persona de un cliente (Accesos, rol «comercio») ve el motivo en palabras, sin el código al lado y sin nombrar
 * al proveedor (`motivoFalloParaCliente`, `accionFalloParaCliente`): leía «Se dio de baja de las promociones 131050»
 * o «Error de Twilio 12345 · Revisalo en la consola de Twilio». El equipo ve el título y el código, como siempre.
 */

import { CheckCircle2 } from "lucide-react";
import type { FallidoPorCodigo } from "../types";
import { accionFalloParaCliente, fallidoSinCargo, motivoFalloParaCliente } from "../logic/textos";
import { num } from "../../campana-nueva/utils/format";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { valorCodigo } from "./BitacoraMensajes";

export function FallidosPorMotivo({ filas, onVerMensajes }: { filas: FallidoPorCodigo[]; onVerMensajes: (codigo: string) => void }) {
  const equipo = useEsEquipoDeenex();
  if (!filas?.length) {
    return (
      <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
        <CheckCircle2 size={15} className="text-emerald-500" /> Ningún mensaje falló.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <th scope="col" className="py-2 pr-3">Cantidad</th>
            <th scope="col" className="py-2 pr-3">Motivo</th>
            <th scope="col" className="py-2 pr-3">Qué se hace</th>
            <th scope="col" className="py-2"><span className="sr-only">Acciones</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.map((f) => (
            <tr key={`${f.codigo}|${f.estado ?? ""}`}>
              <td className="py-2.5 pr-3 font-semibold text-slate-900 tabular-nums align-top">{num(f.cantidad)}</td>
              <td className="py-2.5 pr-3 align-top">
                <span className="text-slate-800">{equipo ? f.titulo : motivoFalloParaCliente(f.titulo)}</span>
                {equipo && <span className="ml-1.5 font-mono text-[11px] text-slate-400">{f.codigo}</span>}
                {fallidoSinCargo(f) && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">No se cobra</span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-slate-600 align-top">{equipo ? f.accionTexto : accionFalloParaCliente(f.accionTexto)}</td>
              <td className="py-2.5 text-right align-top">
                <button type="button" onClick={() => onVerMensajes(valorCodigo(f.codigo))} className="text-xs font-semibold text-violet-600 hover:underline whitespace-nowrap">
                  Ver a quiénes
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default FallidosPorMotivo;
